import { useEffect, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import type { AutocompleteValue } from "@mui/material/Autocomplete";
import type { SelectProps } from "@mui/material/Select";

import { TestProps } from "shared/types";

import TextField from "features/components/TextField";
import { SelectOption } from "features/ui/Select";

interface Props<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> extends TestProps {
  id?: string;
  label?: string;
  options: SelectOption<T>[];
  groupedOptions?: SelectOption<T>[][];
  selectedOption: AutocompleteValue<
    SelectOption<T>,
    Multiple,
    DisableClearable,
    false
  >;
  onSelectedOptionChange: (
    value: AutocompleteValue<SelectOption<T>, Multiple, DisableClearable, false>
  ) => void;
  className?: string;
  width?: number;
  fullWidth?: SelectProps["fullWidth"];
  disabled?: SelectProps["disabled"];
  error?: boolean;
  helperText?: string;
  disableClearable?: DisableClearable;
  placeholder?: string;
  multiple?: Multiple;
  autoFocus?: boolean;
  openOnAutoFocus?: boolean;
  autoWidth?: boolean;
  popperClassName?: string;
  size?: "small" | "medium" | "large";
}

const DEFAULT_WIDTH = 250;

const HORIZONTAL_LINE = <hr className="m-2" />;

const DropdownWithSearch = <
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>({
  label = "",
  options,
  groupedOptions,
  selectedOption,
  onSelectedOptionChange,
  className = "",
  width = DEFAULT_WIDTH,
  fullWidth = false,
  testId,
  placeholder,
  multiple = false as Multiple,
  autoFocus,
  openOnAutoFocus,
  autoWidth = true,
  popperClassName = "",
  size,
  error,
  helperText,
  ...otherProps
}: Props<T, Multiple, DisableClearable>) => {
  const [open, setOpen] = useState(false);
  const textMeasureRef = useRef<HTMLSpanElement>(null);
  const [calculatedWidth, setCalculatedWidth] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (!autoWidth) return;

    if (selectedOption && textMeasureRef.current) {
      // Set the text content to measure
      if (textMeasureRef.current) {
        if (Array.isArray(selectedOption)) {
          textMeasureRef.current.textContent = selectedOption
            .map((option) => option.value.toString())
            .join(", ");
        } else {
          textMeasureRef.current.textContent = selectedOption.value.toString();
        }
      }

      // Add a small buffer to the width
      let measuredWidth = textMeasureRef.current.offsetWidth + 50;

      if (!otherProps.disableClearable) {
        measuredWidth += 10;
      }

      setCalculatedWidth(Math.max(measuredWidth, 100)); // Minimum width of 100px
    } else if (textMeasureRef.current) {
      textMeasureRef.current.textContent = placeholder
        ? placeholder.toString()
        : "";
      const measuredWidth = textMeasureRef.current.offsetWidth + 35;
      setCalculatedWidth(measuredWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (autoFocus && openOnAutoFocus) {
      // we want to wait a bit for input to be rendered to dynamic width
      setTimeout(() => {
        setOpen(true);
      }, 0);
    }
  }, [autoFocus, openOnAutoFocus]);

  if (groupedOptions) {
    const optionsWithGroups: SelectOption<T>[] = [];
    groupedOptions.forEach((opList, index) =>
      opList.forEach((op) => {
        optionsWithGroups.push({ ...op, group: index.toString() });
      })
    );
    options = optionsWithGroups;
  }

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <span
        ref={textMeasureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "inherit",
          fontSize: "inherit",
        }}
      />
      <Autocomplete
        autoFocus={autoFocus}
        autoHighlight={true}
        size={size}
        multiple={multiple}
        open={open}
        disablePortal={false}
        options={options}
        groupBy={(option) => option.group ?? ""}
        fullWidth={fullWidth}
        onOpen={handleOpen}
        onClose={handleClose}
        slotProps={{
          popper: {
            sx: {
              position: "absolute",
              zIndex: 10000,
            },
            className: popperClassName,
          },
          paper: {
            sx: {
              position: "relative",
              minWidth: "15rem",
            },
          },
          listbox: {
            sx: {
              maxHeight: "16rem !important",
              overflowY: "auto",
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            autoFocus={autoFocus}
            {...params}
            label={label}
            size={size}
            error={error}
            helperText={helperText}
            sx={{
              // Select-style control: show the pointer across the whole box,
              // including the padding bands. Target the InputBase root, which
              // MUI defaults to cursor:text.
              "& .MuiInputBase-root": { cursor: "pointer" },
              "& input": { cursor: "pointer" },
              ...(fullWidth
                ? {}
                : autoWidth
                  ? {
                      width: calculatedWidth,
                      maxWidth: "100%",
                      minWidth: "100px",
                    }
                  : { width }),
            }}
            placeholder={placeholder}
            onFocus={(event) => {
              setTimeout(() => {
                event.target.select();
              }, 0);
            }}
          />
        )}
        renderGroup={
          groupedOptions
            ? (params) => (
                <li key={params.key}>
                  {params.group !== "0" && HORIZONTAL_LINE}
                  <ul>{params.children}</ul>
                </li>
              )
            : undefined
        }
        getOptionLabel={(option) => option.value.toString() || ""}
        className={className}
        value={selectedOption}
        onChange={(_, value) => {
          onSelectedOptionChange(value as any);
        }}
        data-testid={testId}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        {...otherProps}
      />
    </>
  );
};

export default DropdownWithSearch;
